import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

export const useUIStore = defineStore('ui', () => {
  // State
  const isSidebarOpen = ref(false)
  const isLoadingGlobal = ref(false)
  const activeTab = ref<string | null>(null)
  const errorModal = reactive({
    visible: false,
    title: '',
    message: '',
    isDismissible: true
  })
  const confirmModal = reactive({
    visible: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: () => {},
    onCancel: () => {}
  })

  /**
   * Set global loading state
   */
  function setLoading(status: boolean) {
    isLoadingGlobal.value = status
  }

  /**
   * Toggle sidebar open/closed
   */
  function toggleSidebar() {
    isSidebarOpen.value = !isSidebarOpen.value
  }

  /**
   * Set sidebar state
   */
  function setSidebar(status: boolean) {
    isSidebarOpen.value = status
  }

  /**
   * Set active tab
   */
  function setActiveTab(tabId: string) {
    activeTab.value = tabId
  }

  /**
   * Show error modal
   */
  function showError(message: string, title = 'Error', isDismissible = true) {
    errorModal.visible = true
    errorModal.title = title
    errorModal.message = message
    errorModal.isDismissible = isDismissible
  }

  /**
   * Hide error modal
   */
  function hideError() {
    errorModal.visible = false
  }

  /**
   * Show confirmation modal
   */
  function showConfirm(options: {
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    onConfirm: () => void
    onCancel?: () => void
  }) {
    confirmModal.visible = true
    confirmModal.title = options.title
    confirmModal.message = options.message
    confirmModal.confirmText = options.confirmText || 'Confirm'
    confirmModal.cancelText = options.cancelText || 'Cancel'
    confirmModal.onConfirm = options.onConfirm
    confirmModal.onCancel = options.onCancel || (() => {})
  }

  /**
   * Confirm action in confirmation modal
   */
  function confirmAction() {
    const callback = confirmModal.onConfirm
    hideConfirm()
    callback()
  }

  /**
   * Cancel action in confirmation modal
   */
  function cancelAction() {
    const callback = confirmModal.onCancel
    hideConfirm()
    callback()
  }

  /**
   * Hide confirmation modal
   */
  function hideConfirm() {
    confirmModal.visible = false
  }

  /**
   * Reset all UI state to defaults
   */
  function resetUI() {
    isSidebarOpen.value = false
    isLoadingGlobal.value = false
    activeTab.value = null
    errorModal.visible = false
    confirmModal.visible = false
  }

  return {
    // State
    isSidebarOpen,
    isLoadingGlobal,
    activeTab,
    errorModal,
    confirmModal,

    // Methods
    setLoading,
    toggleSidebar,
    setSidebar,
    setActiveTab,
    showError,
    hideError,
    showConfirm,
    confirmAction,
    cancelAction,
    hideConfirm,
    resetUI
  }
})
